import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';

import { FlagsList, PanelTitle } from 'ui';
import { MessageModal } from 'ui/Modals';
import { EmptyDataLabel } from 'ui/Labels';
import { fetchUserImage } from 'services';
import { personalAdvisorPicturesPath } from 'shared/constants/picturesPath';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 38,
    height: 38,
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  compactButtons: {
    marginLeft: 'auto',
  },
  noPadding: {
    padding: 0,
  },
}));

const ContactBox = ({
  analystTeams,
  compact,
  firstName,
  lastName,
  followed,
  jobFunction,
  jobTitle,
  languages,
  showRequestCallButton,
  showFollowButton,
  showMessageButton,
  userId,
  onRequestCall,
  onSendMessage,
  empty,
  internal,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const toggleMessageModal = () => setMessageModalOpen(!messageModalOpen);

  const fetchImage = async () => {
    try {
      const url = await fetchUserImage(userId);
      setImageUrl(url);
    } catch (e) {
      setImageUrl(null);
    }
  };

  useEffect(() => {
    if (userId) fetchImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const showActions = () => showFollowButton || showMessageButton || showRequestCallButton;

  const renderButtons = () =>
    showActions() && (
      <CardActions className={compact ? classes.noPadding : null}>
        {showMessageButton && (
          <Button onClick={toggleMessageModal} variant="contained" size="small" color="primary">
            <EmailIcon className={classes.buttonIcon} />
            {t('Message')}
          </Button>
        )}
        {showFollowButton && (
          <>
            {followed ? (
              <Button variant="outlined" size="small" color="primary">
                {t('Unfollow')}
              </Button>
            ) : (
              <Button variant="contained" size="small" color="primary">
                {t('Follow')}
              </Button>
            )}
          </>
        )}
        {showRequestCallButton && (
          <Button onClick={onRequestCall} variant="contained" size="small" color="primary">
            <PhoneIcon className={classes.buttonIcon} />
            {t('Request call')}
          </Button>
        )}
      </CardActions>
    );

  const renderAvatar = imageUrl ? (
    <Avatar className={classes.avatar} src={imageUrl} />
  ) : (
    <Avatar className={classes.avatar}>
      <AccountCircleIcon />
    </Avatar>
  );

  const renderContent = (
    <>
      {jobFunction && <PanelTitle>{jobFunction}</PanelTitle>}
      <Card>
        <CardContent>
          <Grid container spacing={1}>
            <Grid item>
              {internal ? (
                <Avatar className={classes.avatar} src={personalAdvisorPicturesPath} />
              ) : (
                renderAvatar
              )}
            </Grid>
            <Grid item>
              <Typography variant="body1" component="h2">
                {`${firstName} ${lastName}`}
              </Typography>
              {jobTitle && (
                <Typography variant="body2" color="textSecondary" component="p">
                  {jobTitle}
                </Typography>
              )}
              {analystTeams && (
                <Typography variant="body2" color="textSecondary" component="p">
                  {analystTeams.join(', ')}
                </Typography>
              )}
              {languages && <FlagsList countryCodes={languages} />}
            </Grid>
            {compact && (
              <Grid item className={classes.compactButtons}>
                {renderButtons()}
              </Grid>
            )}
          </Grid>
        </CardContent>
        <Divider />
        {!compact && renderButtons()}
      </Card>
      <MessageModal
        jobFunction={jobFunction}
        open={messageModalOpen}
        onClose={toggleMessageModal}
        onSendMessage={onSendMessage}
      />
    </>
  );

  return empty ? (
    <Paper>
      <List>
        <EmptyDataLabel label={t('There are no analyst yet.')} />
      </List>
    </Paper>
  ) : (
    renderContent
  );
};

ContactBox.defaultProps = {
  analystTeams: null,
  compact: false,
  followed: false,
  jobFunction: null,
  jobTitle: null,
  languages: null,
  showRequestCallButton: true,
  showFollowButton: false,
  showMessageButton: true,
  userId: null,
  onRequestCall: () => null,
  onSendMessage: () => null,
  empty: false,
  internal: false,
};

ContactBox.propTypes = {
  analystTeams: PropTypes.arrayOf(PropTypes.string),
  compact: PropTypes.bool,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  followed: PropTypes.bool,
  jobFunction: PropTypes.string,
  jobTitle: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string),
  showRequestCallButton: PropTypes.bool,
  onRequestCall: PropTypes.func,
  onSendMessage: PropTypes.func,
  showFollowButton: PropTypes.bool,
  showMessageButton: PropTypes.bool,
  userId: PropTypes.string,
  empty: PropTypes.bool,
  internal: PropTypes.bool,
};

export default ContactBox;
